import { MessageSquare, BookOpen, Settings, Brain, Play, BarChart } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";

const HowToUseSection = () => {
  const steps = [
    {
      number: 1,
      icon: BookOpen,
      title: "Enter Your Topic",
      description: "Write what you want to learn about in the text box. Be specific! For example: 'Python basics - variables, loops, and functions'."
    },
    {
      number: 2,
      icon: Settings,
      title: "Choose Settings",
      description: "Select how many questions you want (1-50), pick difficulty level (Easy, Medium, Hard, or Mix), and set your timer."
    },
    {
      number: 3,
      icon: Brain,
      title: "AI Generates Quiz",
      description: "Click 'Generate Quiz' button. Our AI will create personalized questions for you in 40-60 seconds."
    },
    {
      number: 4,
      icon: Play,
      title: "Take the Quiz",
      description: "Answer all questions before time runs out. You can mark questions for review and navigate between them freely."
    },
    {
      number: 5,
      icon: BarChart,
      title: "View Your Results",
      description: "See your score, review correct answers, and learn from detailed explanations. Save the quiz to track your progress!"
    }
  ];

  const faqs = [
    {
      question: "What is MCQ AI?",
      answer: "MCQ AI is an intelligent quiz generation platform that uses artificial intelligence to create personalized multiple-choice questions on any topic. Simply describe what you want to learn or test your knowledge on, and our AI will generate relevant questions in seconds."
    },
    {
      question: "How do I create a quiz?",
      answer: "Creating a quiz is simple: (1) Enter a detailed description of your topic in the text area, (2) Select the number of questions you want (5-20), (3) Choose the difficulty level (Easy, Medium, Hard, or Mix), (4) Set your timer duration, (5) Enable or disable negative marking based on your preference, and (6) Click 'Generate Quiz' to start!"
    },
    {
      question: "What difficulty levels are available?",
      answer: "We offer four difficulty levels: Easy (suitable for beginners), Medium (balanced challenge), Hard (advanced topics), and Mix (a combination of all difficulty levels for comprehensive testing)."
    },
    {
      question: "How does the timer work?",
      answer: "The timer automatically adjusts based on the number of questions - each question gets 1 minute by default. You can manually adjust the timer using the +/- buttons. The quiz will automatically submit when time runs out, so make sure to manage your time wisely!"
    },
    {
      question: "What is negative marking?",
      answer: "Negative marking deducts points for incorrect answers. When enabled, you can set a custom penalty (default is 0.25 points per wrong answer). This feature helps simulate real exam conditions and encourages careful answering."
    },
    {
      question: "Can I review my answers after completing a quiz?",
      answer: "Yes! After completing a quiz, you'll see a detailed results page showing your score, time taken, correct answers, wrong answers, and unanswered questions. You can also review each question with the correct answer and an AI-generated explanation to help you understand the concepts better."
    },
    {
      question: "Can I retake a quiz?",
      answer: "Absolutely! On the results page, you'll find a 'Retake Quiz' button that lets you attempt the same quiz again. You can also view your previous attempts in the 'Attempt History' section on your dashboard."
    },
    {
      question: "Can I share quizzes with others?",
      answer: "Yes! After generating a quiz, you can share it with friends, students, or colleagues using the share button. Recipients can take the quiz even without creating an account, and their results will be tracked separately."
    },
    {
      question: "Do I need to sign up to use MCQ AI?",
      answer: "You can try MCQ AI without signing up, but creating an account unlocks powerful features like saving your quizzes, tracking your progress over time, viewing detailed attempt history, and accessing your quizzes from any device."
    },
    {
      question: "How can I improve my quiz topic description?",
      answer: "The more specific and detailed your topic description, the better the AI-generated questions will be. Instead of 'JavaScript', try 'JavaScript ES6 features including arrow functions, destructuring, and promises'. Include specific subtopics, concepts, or learning objectives you want to focus on."
    }
  ];

  return (
    <section id="how-to-use" className="py-12 sm:py-16 lg:py-20 px-2 sm:px-3 lg:px-4 bg-muted/30">
      <div className="container mx-auto max-w-6xl px-0">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 sm:mb-6">
            How to Use MCQ AI
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto">
            Follow these simple steps to create and take quizzes with AI
          </p>
        </div>

        {/* Step by Step Guide */}
        <div className="mb-12 sm:mb-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-10">
            Step-by-Step Guide
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card key={index} className="bg-gradient-card backdrop-blur-sm border-border/50 p-6 shadow-card hover:shadow-elegant transition-smooth">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xl font-bold text-primary">{step.number}</span>
                    </div>
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3">{step.title}</h4>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq" className="max-w-4xl mx-auto scroll-mt-20">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-10">
            Frequently Asked Questions
          </h3>
          <div className="bg-card rounded-2xl sm:rounded-3xl p-4 sm:p-5 lg:p-6 shadow-card border border-border">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-border last:border-0">
                  <AccordionTrigger className="text-left hover:no-underline hover:text-primary py-5 sm:py-6 [&[data-state=open]]:text-primary">
                    <div className="flex items-center gap-3 sm:gap-4 w-full">
                      <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                      <span className="font-semibold text-base sm:text-lg text-left flex-1">{faq.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pl-9 sm:pl-10 pr-2 pb-5 sm:pb-6 pt-0 text-sm sm:text-base leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
